import React, { useEffect } from 'react'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

import useMutation from '@hooks/useGQLMutation'
import useFormWithErrors from '@hooks/useFormWithErrors'

import { serverErrorsToFormErrors } from '@frontend/adapters/serverErrorsAdapter'

import { scopedTranslation } from '@utils/I18n'

import { useErrorsStore } from '@stores/errorsStore'

import { Button } from '@atoms'
import Tag from '@atoms/Tag'

import { UpdateSupporterTagsDocument } from '@frontend/graphql/mutations/updateSupporterTags.generated'
import { useSupporterReload } from './SupporterReloadContext'
import EditableTagsField from './EditableTagsField'

const t = scopedTranslation('shared')
const tAttributes = scopedTranslation('attributes')

type TagsFieldRowType = {
  supporterId: string | number
  groupId?: string | number
  canEdit?: boolean
  tags: Array<{ value: string; label: string }>
}

export default function TagsFieldRow({ supporterId, canEdit, tags }: TagsFieldRowType) {
  const [open, setOpen] = React.useState(false)
  const reloadSupporter = useSupporterReload()

  const editButtonRef = React.useRef<HTMLButtonElement | null>(null)

  const validations = yup.object().shape({
    supporter: yup.object({
      tags: yup
        .array()
        .of(
          yup.object({
            value: yup.string(),
            label: yup.string(),
          })
        )
        .label(tAttributes('supporter.tags')),
    }),
  })

  const {
    setErrors: setFormErrors,
    handleSubmit,
    control,
    setValue: setFormValue,
  } = useFormWithErrors({
    resolver: yupResolver(validations),
    mode: 'onTouched',
    defaultValues: {
      supporter: {
        tags: tags ?? [],
      },
    },
  })

  const { setErrors: setAppErrors, clearErrors: clearAppErrors } = useErrorsStore(({ setErrors, clearErrors }) => ({
    setErrors,
    clearErrors,
  }))

  const onSuccess = () => {
    setOpen(false)
    reloadSupporter()
    editButtonRef.current?.focus()
  }

  useEffect(() => {
    setFormValue('supporter.tags', tags)
  }, [tags, setFormValue])

  const { mutate } = useMutation<typeof UpdateSupporterTagsDocument>(UpdateSupporterTagsDocument)

  const onSubmit = async (values: any) => {
    clearAppErrors('supporterTags')

    mutate(
      {
        id: supporterId,
        ...values.supporter,
        tags: values.supporter.tags.map((tag: any) => tag.value),
      },
      {
        onSuccess: (response) => {
          if (response.updateSupporterTags?.success) {
            onSuccess()
            clearAppErrors('supporterTags')
          } else if (response.updateSupporterTags) {
            setFormErrors({
              errorsBasePath: 'supporterTags',
              errors: response.updateSupporterTags.errors,
              adapter: serverErrorsToFormErrors,
            })
            setAppErrors({
              errorsPath: 'supporterTags',
              errors: response.updateSupporterTags.errors,
            })
          } else {
            setAppErrors({
              errorsPath: 'supporterTags',
              errors: [
                {
                  message: '',
                  code: 'unknown',
                },
              ],
            })
          }
        },
        onError: () => {
          setAppErrors({
            errorsPath: 'supporterTags',
            errors: [
              {
                message: '',
                code: 'unknown',
              },
            ],
          })
        },
      }
    )
  }

  const onCancel = () => {
    setOpen(false)
    setTimeout(() => {
      editButtonRef.current?.focus()
    })
    setFormValue('supporter.tags', tags ?? [])
  }

  const onEdit = () => {
    setOpen(true)
  }

  return (
    <div
      data-testid="tags-form"
      className="tw:grid tw:grid-cols-5 tw:grid-rows-1 tw:gap-4 tw:items-center tw:px-8 tw:py-5 tw:shadow-xs tw:rounded-xl tw:border tw:border-gray-200"
    >
      <div>
        <h3 className="tw:font-medium">{tAttributes('supporter.tags')}</h3>
      </div>
      {open ? (
        <div className="tw:col-span-3">
          <EditableTagsField
            name="supporter.tags"
            onSubmit={handleSubmit(onSubmit)}
            label={tAttributes('supporter.tags')}
            control={control}
            onCancel={onCancel}
          />
        </div>
      ) : (
        <div className="tw:col-span-3">
          <p className="tw:flex tw:gap-2 tw:flex-wrap">
            {tags?.map((tag) => (
              <Tag key={tag.value} size="sm">
                {tag.label}
              </Tag>
            ))}
          </p>
        </div>
      )}

      <div className="tw:col-start-5 tw:flex tw:justify-end">
        <Button
          rank="secondary"
          leadingIcon="pencil-02"
          onClick={onEdit}
          ref={editButtonRef}
          disabled={canEdit ? open : true}
          ariaLabel={t('aria_labels.edit_object', { name: tAttributes('supporter.tags') })}
          dataCompId="supporter-show_tags-field-row_edit"
        >
          {t('buttons.edit')}
        </Button>
      </div>
    </div>
  )
}
